import { OwnApartment } from '@models/apartment.types';
import { BuildingSearchResponse } from '@models/api';
import { PaginatedResponse } from '@models/api/data.types';
import { LanguageKey } from '@models/Language';
import { ApartmentProduct } from '@models/satofi/apartmentProduct.types';
import { createAsyncTask } from '@stores/taskStore';

import {
  APARTMENTS_API_BASE_URL,
  REAL_ESTATE_API_BASE_URL,
} from './api.constants';
import { generateApiUrl } from './api.utils';
import { authClient, publicClient } from './client.api';
import { REAL_ESTATE_V2_API_URL } from './parking.api';

export const APARTMENTS_API_URLS = {
  FETCH_APARTMENTS: `${APARTMENTS_API_BASE_URL}myapartments`,
  APARTMENT_PRODUCTS: `${APARTMENTS_API_BASE_URL}products/:apartmentProductId`,
  REAL_ESTATE_SEARCH: `${REAL_ESTATE_API_BASE_URL}/homes/places/site-search`,
  APARTMENT_RECOMMENDATIONS_NEAR: `${REAL_ESTATE_V2_API_URL}/product/:productId/similar-near`,
  APARTMENT_RECOMMENDATIONS_FAR: `${REAL_ESTATE_V2_API_URL}/product/:productId/similar-longer`,
  REAL_ESTATE_BY_ID: `${REAL_ESTATE_V2_API_URL}/real-estate/:realEstateId`,
} as const;

export const realEstatesApi = {
  getApartments: createAsyncTask('get-own-apartments', () =>
    authClient.get<{ ownApartments: OwnApartment[] }>(
      APARTMENTS_API_URLS.FETCH_APARTMENTS
    )
  ),

  getApartmentProducts: (productId: string) =>
    authClient.get<unknown>(APARTMENTS_API_URLS.APARTMENT_PRODUCTS, {
      params: { apartmentProductId: productId },
    }),

  searchRealEstates: (params: { text: string; maxResults: number }) =>
    publicClient.get<BuildingSearchResponse>(
      APARTMENTS_API_URLS.REAL_ESTATE_SEARCH,
      { params }
    ),
  /**
   * Fetches apartment recommendations for similar apartments as the given apartment product ID.
   * Gets similar apartments from the same real-estate or from the vicinity.
   *
   * GET `/api/realestates/v2/product/:apartmentProductId/similar-near`
   */
  getApartmentRecommendationsNear: (
    apartmentProductId: string,
    language: LanguageKey
  ) =>
    publicClient.get<PaginatedResponse<ApartmentProduct>>(
      generateApiUrl(APARTMENTS_API_URLS.APARTMENT_RECOMMENDATIONS_NEAR, {
        productId: apartmentProductId,
      }) + `?lang=${language}`
    ),

  /**
   * Fetches apartment recommendations for similar apartments as the given apartment product ID.
   * Gets apartments from similar areas farther than `getApartmentRecommendationsNear`
   *
   * GET `/api/realestates/v2/product/:apartmentProductId/similar-longer`
   */
  getApartmentRecommendationsFar: (
    apartmentProductId: string,
    language: LanguageKey
  ) =>
    publicClient.get<PaginatedResponse<ApartmentProduct>>(
      generateApiUrl(APARTMENTS_API_URLS.APARTMENT_RECOMMENDATIONS_FAR, {
        productId: apartmentProductId,
      }) + `?lang=${language}`
    ),
};
